import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import Select from "react-select";
import { MdRefresh } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { customMultiSelectStyles } from "commons/styles/CustomStyles";
import { SELECT_ALL } from "commons/table/Constants";
import { AgentWorkspaceConstants } from "lookup/AgentWorkspaceConstants";
import { useAgentNextCallGroupFlow } from "hooks/UseAgentNextCallGroupFlow";
import { sendAuthenticatedAsyncRequestWithFinallyCallback } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import {
  buildNextCallGroupFiltered,
  clientOptionsForFilters,
  clientOptionsWithSelectAll,
  normalizeSelectValue,
  populationTypeSelectOptions,
  resolveClientSelectionChange,
  toSelectOptions,
} from "./callGroupFilterHelpers";
import {
  loadStickyFilterSelections,
  saveStickyFilterSelections,
} from "./callGroupFilterSessionStorage";
import AssignedActivitiesTable from "./AssignedActivitiesTable";
import FilteredCallGroupsCompleteModal from "./FilteredCallGroupsCompleteModal";

const FILTER_PLACEHOLDERS = {
  customerNameAbbreviation: "All customers",
  clientNameAbbreviation: "All clients",
  populationType: "All population types",
  type: "All types",
  status: "All statuses",
  entityType: "All entities",
};

const FILTER_LABELS = {
  customerNameAbbreviation: "Customer",
  clientNameAbbreviation: "Client",
  populationType: "Population Type",
  type: "Type",
  status: "Status",
  entityType: "Entity",
};

/** Layout: Customer / Population Type / Client, then Type / Status / Entity. */
const FILTER_ROWS = [
  ["customerNameAbbreviation", "populationType", "clientNameAbbreviation"],
  ["type", "status", "entityType"],
];

const emptySelections = () =>
  AgentWorkspaceConstants.AGENT_NEXT_CALL_GROUP_FILTER_IDS.reduce((acc, id) => {
    acc[id] = [];
    return acc;
  }, {});

/**
 * Drops sticky options that are no longer present in the current filter metadata.
 */
function pruneSelectionsAgainstMeta(selections, filtersMeta) {
  if (!filtersMeta || !selections) {
    return emptySelections();
  }
  const next = emptySelections();
  next.customerNameAbbreviation = normalizeSelectValue(
    selections.customerNameAbbreviation
  ).filter((opt) =>
    (filtersMeta.customerNameAbbreviation || []).some((row) => row.id === opt.value)
  );
  const allowedPopulationValues = new Set(
    populationTypeSelectOptions().map((o) => o.value)
  );
  next.populationType = normalizeSelectValue(selections.populationType).filter(
    (opt) => allowedPopulationValues.has(opt.value)
  );
  const allowedClients = new Set(
    clientOptionsForFilters(
      filtersMeta,
      next.customerNameAbbreviation,
      next.populationType
    ).map((o) => o.value)
  );
  next.clientNameAbbreviation = normalizeSelectValue(
    selections.clientNameAbbreviation
  ).filter((opt) => opt.value !== SELECT_ALL && allowedClients.has(opt.value));
  for (const id of ["type", "status", "entityType"]) {
    const metaRows = filtersMeta[id] || [];
    next[id] = normalizeSelectValue(selections[id]).filter((opt) =>
      metaRows.some((row) => row.id === opt.value)
    );
  }
  return next;
}

const CallGroupFilterScreen = () => {
  const navigate = useNavigate();
  const [flowEnabled, flowLoading] = useAgentNextCallGroupFlow();
  const [filtersMeta, setFiltersMeta] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [selections, setSelections] = useState(emptySelections);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  useEffect(() => {
    if (flowLoading) {
      return;
    }
    if (!flowEnabled) {
      navigate("/agent-workspace/list", { replace: true });
    }
  }, [flowEnabled, flowLoading, navigate]);

  useEffect(() => {
    if (!flowEnabled) {
      return;
    }
    setFiltersLoading(true);
    sendAuthenticatedAsyncRequestWithFinallyCallback(
      "/agent-workspace/call-group/list-filters?showBioSimilar=false",
      "GET",
      null,
      (response) => {
        const meta = response.data.filters || {};
        setFiltersMeta(meta);
        const sticky = loadStickyFilterSelections();
        setSelections(
          sticky ? pruneSelectionsAgainstMeta(sticky, meta) : emptySelections()
        );
      },
      (error) => {
        showNotification({
          title: "Error fetching filters",
          message: getErrorMessage(error) || "Unable to load call group filters.",
          position: "tr",
          type: "error",
        });
      },
      () => setFiltersLoading(false)
    );
  }, [flowEnabled]);

  const optionMaps = useMemo(() => {
    if (!filtersMeta) {
      return {};
    }
    return {
      customerNameAbbreviation: toSelectOptions(filtersMeta.customerNameAbbreviation),
      clientNameAbbreviation: clientOptionsWithSelectAll(
        filtersMeta,
        selections.customerNameAbbreviation,
        selections.populationType
      ),
      populationType: populationTypeSelectOptions(),
      type: toSelectOptions(filtersMeta.type),
      // Same option set as Call Group List (includes ALL for status).
      status: toSelectOptions(filtersMeta.status),
      entityType: toSelectOptions(filtersMeta.entityType),
    };
  }, [
    filtersMeta,
    selections.customerNameAbbreviation,
    selections.populationType,
  ]);

  const hasActiveFilters = useMemo(
    () =>
      AgentWorkspaceConstants.AGENT_NEXT_CALL_GROUP_FILTER_IDS.some(
        (id) => normalizeSelectValue(selections[id]).length > 0
      ),
    [selections]
  );

  const pruneClientsAgainstParents = (next) => {
    const allowed = new Set(
      clientOptionsForFilters(
        filtersMeta,
        next.customerNameAbbreviation,
        next.populationType
      ).map((o) => o.value)
    );
    next.clientNameAbbreviation = normalizeSelectValue(
      next.clientNameAbbreviation
    ).filter((c) => c.value !== SELECT_ALL && allowed.has(c.value));
    return next;
  };

  const handleSelectionChange = (filterId, selected, actionMeta) => {
    setShowCompleteModal(false);
    setSelections((prev) => {
      let nextSelected = selected;
      if (filterId === "clientNameAbbreviation") {
        nextSelected = resolveClientSelectionChange(
          selected,
          actionMeta,
          optionMaps.clientNameAbbreviation
        );
      }
      let next = { ...prev, [filterId]: nextSelected };
      if (
        filterId === "customerNameAbbreviation" ||
        filterId === "populationType"
      ) {
        next = pruneClientsAgainstParents(next);
      }
      saveStickyFilterSelections(next);
      return next;
    });
  };

  const handleClearFilters = () => {
    const cleared = emptySelections();
    setShowCompleteModal(false);
    setSelections(cleared);
    saveStickyFilterSelections(cleared);
  };

  const handleGo = (event) => {
    event.preventDefault();
    if (submittingRef.current) {
      return;
    }
    setShowCompleteModal(false);
    submittingRef.current = true;
    setSubmitting(true);
    saveStickyFilterSelections(selections);
    const filtered = buildNextCallGroupFiltered(selections);
    sendAuthenticatedAsyncRequestWithFinallyCallback(
      "/agent-workspace/call-group/next",
      "POST",
      { filtered },
      (response) => {
        if (response.status === 204 || !response.data?.callGroupId) {
          setShowCompleteModal(true);
          return;
        }
        const statusFilter = filtered.find((f) => f.id === "status")?.value ?? null;
        navigate(`/agent-workspace/call-group-details/${response.data.callGroupId}`, {
          state: { status: statusFilter, fromAgentFilterScreen: true },
        });
      },
      (error) => {
        if (error?.status === 404) {
          showNotification({
            title: "Feature unavailable",
            message: "The next call group flow is not enabled for this environment.",
            position: "tr",
            type: "warning",
          });
          navigate("/agent-workspace/list", { replace: true });
          return;
        }
        showNotification({
          title: "Unable to load next call group",
          message: getErrorMessage(error) || "Please try again.",
          position: "tr",
          type: "error",
        });
      },
      () => {
        submittingRef.current = false;
        setSubmitting(false);
      }
    );
  };

  const FILTER_TEST_IDS = {
    customerNameAbbreviation: "filter-customer",
    clientNameAbbreviation: "filter-client",
    populationType: "filter-population-type",
    type: "filter-type",
    status: "filter-status",
    entityType: "filter-entity-type",
  };

  const renderFilterSelect = (filterId) => (
    <Form.Group className="mb-3" controlId={`filter-${filterId}`} key={filterId}>
      <Form.Label className="fw-semibold">{FILTER_LABELS[filterId]}</Form.Label>
      <div data-testid={FILTER_TEST_IDS[filterId]}>
        <Select
          isMulti
          isClearable
          closeMenuOnSelect={filterId !== "clientNameAbbreviation"}
          options={optionMaps[filterId] || []}
          value={selections[filterId]}
          onChange={(selected, actionMeta) =>
            handleSelectionChange(filterId, selected || [], actionMeta)
          }
          styles={customMultiSelectStyles}
          placeholder={FILTER_PLACEHOLDERS[filterId]}
          classNamePrefix="agent-filter"
          inputId={`agent-filter-${filterId}`}
        />
      </div>
    </Form.Group>
  );

  if (flowLoading || !flowEnabled) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4" data-testid="agent-filter-screen-root">
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-1 gap-3">
            <h4 className="mb-0 fw-bold">Agent Workspace Filter Screen</h4>
            <Button
              type="button"
              variant="outline-dark"
              size="sm"
              className="d-inline-flex align-items-center flex-shrink-0 px-3"
              onClick={handleClearFilters}
              disabled={filtersLoading || !hasActiveFilters}
              data-testid="btn-clear-filters"
              aria-label="Clear filters"
            >
              <MdRefresh size={18} className="me-1" aria-hidden="true" />
              Clear Filters
            </Button>
          </div>
          <p className="text-muted mb-4">
            Filter the call group pool, then start the next assignment
          </p>

          {filtersLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <Form onSubmit={handleGo}>
              {FILTER_ROWS.map((rowIds) => (
                <Row key={rowIds.join("-")}>
                  {rowIds.map((filterId) => (
                    <Col md={4} key={filterId}>
                      {renderFilterSelect(filterId)}
                    </Col>
                  ))}
                </Row>
              ))}

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || filtersLoading}
                  data-testid="btn-start-your-work"
                >
                  {submitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Starting…
                    </>
                  ) : (
                    "Start Your Work"
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <AssignedActivitiesTable enabled={flowEnabled} />
      <FilteredCallGroupsCompleteModal
        show={showCompleteModal}
        onOkay={() => setShowCompleteModal(false)}
      />
    </Container>
  );
};

export default CallGroupFilterScreen;
